import { atom } from 'jotai';
import { ExportStatus, PlatformType, ExportResult } from '../services/exportService';
import { sceneListAtom, activeProjectAtom, isBlankModeAtom } from './sceneAtoms';

/**
 * Platform Export State Atoms
 * Manages video export queue, progress, and platform-specific settings
 */

// Export Queue State
export const exportQueueAtom = atom<ExportStatus[]>([]);
export const activeExportIdAtom = atom<string | null>(null);

// Platform Selection
export const selectedPlatformAtom = atom<PlatformType>('youtube');

// Export Modal Visibility
export const isExportModalVisibleAtom = atom<boolean>(false);
export const isExportProgressVisibleAtom = atom<boolean>(false);

// Export Results
export const completedExportsAtom = atom<ExportResult[]>([]);

// Export Input
export const exportVideoBlobsAtom = atom<Blob[]>([]);
export const exportFilenameAtom = atom<string>('');

// Derived Atoms
export const activeExportAtom = atom<ExportStatus | null>((get) => {
  const activeId = get(activeExportIdAtom);
  const queue = get(exportQueueAtom);
  return queue.find(exp => exp.exportId === activeId) || null;
});

export const hasActiveExportsAtom = atom<boolean>((get) => {
  const queue = get(exportQueueAtom);
  return queue.some(exp => 
    exp.status === 'queued' || 
    exp.status === 'processing' || 
    exp.status === 'encoding'
  );
});

export const completedExportsCountAtom = atom<number>((get) => {
  const queue = get(exportQueueAtom);
  return queue.filter(exp => exp.status === 'completed').length;
});

export const failedExportsCountAtom = atom<number>((get) => {
  const queue = get(exportQueueAtom);
  return queue.filter(exp => exp.status === 'failed').length;
});

// Action Atoms
export const addToExportQueueAtom = atom(
  null,
  (get, set, exportStatus: ExportStatus) => {
    const queue = get(exportQueueAtom);
    set(exportQueueAtom, [...queue, exportStatus]);
  }
);

export const updateExportStatusAtom = atom(
  null,
  (get, set, update: { exportId: string; status: Partial<ExportStatus> }) => {
    const queue = get(exportQueueAtom);
    const updated = queue.map(exp => 
      exp.exportId === update.exportId 
        ? { ...exp, ...update.status }
        : exp
    );
    set(exportQueueAtom, updated);
  }
);

export const removeFromExportQueueAtom = atom(
  null,
  (get, set, exportId: string) => {
    const queue = get(exportQueueAtom);
    set(exportQueueAtom, queue.filter(exp => exp.exportId !== exportId));
  }
);

export const clearCompletedExportsAtom = atom(
  null,
  (get, set) => {
    const queue = get(exportQueueAtom);
    const filtered = queue.filter(exp => 
      exp.status !== 'completed' && 
      exp.status !== 'failed' && 
      exp.status !== 'cancelled'
    );
    set(exportQueueAtom, filtered);
  }
);

export const resetExportStateAtom = atom(
  null,
  (get, set) => {
    set(exportQueueAtom, []);
    set(activeExportIdAtom, null);
    set(selectedPlatformAtom, 'youtube');
    set(isExportModalVisibleAtom, false);
    set(isExportProgressVisibleAtom, false);
    set(completedExportsAtom, []);
    set(exportVideoBlobsAtom, []);
    set(exportFilenameAtom, '');
    // Reset Dadan export state
    set(dadanExportStatusAtom, 'idle');
    set(dadanExportProgressAtom, 0);
    set(dadanExportProgressMessageAtom, '');
    set(dadanExportJobIdAtom, null);
    set(dadanExportResultAtom, null);
    set(isDadanExportPopoverOpenAtom, false);
  }
);

// ==================== DADAN EXPORT ATOMS ====================
// State management for exporting stitched video to Dadan platform

export type DadanExportStatus = 
  | 'idle'
  | 'preparing'
  | 'queued'
  | 'stitching'
  | 'uploading'
  | 'complete'
  | 'failed';

export interface DadanExportResult {
  success: boolean;
  videoId?: string;
  watchUrl?: string;
  shareUrl?: string;
  error?: string;
}

export interface DadanExportMetrics {
  totalDuration: number;
  sceneCount: number;
  estimatedSize: number;
  quality: '720p' | '1080p' | '2k';
}

// Dadan Export UI state
export const dadanExportStatusAtom = atom<DadanExportStatus>('idle');
export const dadanExportProgressAtom = atom<number>(0);
export const dadanExportProgressMessageAtom = atom<string>('');
export const dadanExportJobIdAtom = atom<string | null>(null);
export const dadanExportResultAtom = atom<DadanExportResult | null>(null);
export const isDadanExportPopoverOpenAtom = atom<boolean>(false);
// Track which project the export is for (prevents popover flashing on project switch)
export const dadanExportProjectIdAtom = atom<number | null>(null);

// Dadan Export options
export const dadanExportChaptersEnabledAtom = atom<boolean>(true);
export const dadanExportQualityAtom = atom<'720p' | '1080p' | '2k'>('1080p');

// Derived atom: Check if project has recorded scenes
export const hasRecordedScenesForExportAtom = atom((get) => {
  const scenes = get(sceneListAtom);
  return scenes.some(scene => scene.status === 'recorded');
});

// Derived atom: Count of recorded scenes
export const recordedScenesForExportCountAtom = atom((get) => {
  const scenes = get(sceneListAtom);
  return scenes.filter(scene => scene.status === 'recorded').length;
});

// Derived atom: Total duration of recorded scenes with transition overlaps (in milliseconds)
// This matches the calculation in SlidesRail - transitions overlap, so we subtract their duration
export const totalRecordedDurationForExportAtom = atom((get) => {
  const scenes = get(sceneListAtom);
  const recordedScenes = scenes.filter(scene => scene.status === 'recorded');
  
  let totalDurationMs = 0;
  let transitionOverlapMs = 0;
  
  recordedScenes.forEach((scene, index) => {
    totalDurationMs += scene.duration || 0;
    
    // Subtract transition overlap for all scenes except the last
    // Transition causes overlap between current scene's end and next scene's start
    // Check both scene.transitionOut and scene.metadata?.transitionOut for compatibility
    const transitionOut = scene.transitionOut || (scene as any).metadata?.transitionOut;
    if (index < recordedScenes.length - 1 && transitionOut) {
      const transitionType = transitionOut.type || 'cut';
      // Only crossfade, slide, and fade transitions cause overlap; cut/instant are immediate
      if (transitionType === 'crossfade' || transitionType === 'slide' || transitionType === 'fade') {
        const transitionDurationSec = transitionOut.duration || 0;
        transitionOverlapMs += transitionDurationSec * 1000;
      }
    }
  });
  
  return Math.max(0, totalDurationMs - transitionOverlapMs);
});

// Derived atom: Export metrics
export const dadanExportMetricsAtom = atom<DadanExportMetrics>((get) => {
  const scenes = get(sceneListAtom);
  const recordedScenes = scenes.filter(scene => scene.status === 'recorded');
  const totalDuration = get(totalRecordedDurationForExportAtom);
  const quality = get(dadanExportQualityAtom);
  
  // Estimate file size based on duration and quality
  const bitrateMap = { '720p': 4, '1080p': 8, '2k': 16 };
  const bitrateMbps = bitrateMap[quality];
  const durationSeconds = totalDuration / 1000;
  const estimatedSizeMB = Math.ceil((bitrateMbps * durationSeconds) / 8);
  
  return {
    totalDuration,
    sceneCount: recordedScenes.length,
    estimatedSize: estimatedSizeMB,
    quality,
  };
});

// Derived atom: Is Dadan export in progress
export const isDadanExportInProgressAtom = atom((get) => {
  const status = get(dadanExportStatusAtom);
  return ['preparing', 'queued', 'stitching', 'uploading'].includes(status);
});

// Derived atom: Check if project exceeds duration limit
// Returns { exceedsLimit, totalDurationMs, maxDurationMs, reason }
// Blank mode detection uses isBlankModeAtom (set by CanvasRecorder from URL param)
// or falls back to checking project metadata.mode
export const projectDurationLimitAtom = atom((get) => {
  const totalDuration = get(totalRecordedDurationForExportAtom);
  const activeProject = get(activeProjectAtom);
  
  // Check isBlankModeAtom first (set from URL param), then fallback to metadata
  const isBlankModeFromAtom = get(isBlankModeAtom);
  const isBlankModeFromMetadata = (activeProject as any)?.metadata?.mode === 'blank';
  const isBlankMode = isBlankModeFromAtom || isBlankModeFromMetadata;
  
  const MAX_PROJECT_DURATION_MS = 60 * 60 * 1000; // 1 hour
  const exceedsLimit = !isBlankMode && totalDuration > MAX_PROJECT_DURATION_MS;
  
  return {
    exceedsLimit,
    totalDurationMs: totalDuration,
    maxDurationMs: MAX_PROJECT_DURATION_MS,
    isBlankMode,
    reason: exceedsLimit 
      ? `Project duration (${Math.ceil(totalDuration / 60000)} min) exceeds maximum allowed (60 min)`
      : undefined,
  };
});

// Derived atom: Can start Dadan export (includes duration limit check)
export const canStartDadanExportAtom = atom((get) => {
  const hasRecordedScenes = get(hasRecordedScenesForExportAtom);
  const isInProgress = get(isDadanExportInProgressAtom);
  const durationLimit = get(projectDurationLimitAtom);
  return hasRecordedScenes && !isInProgress && !durationLimit.exceedsLimit;
});

// Action atom: Reset Dadan export state
export const resetDadanExportStateAtom = atom(
  null,
  (get, set) => {
    set(dadanExportStatusAtom, 'idle');
    set(dadanExportProgressAtom, 0);
    set(dadanExportProgressMessageAtom, '');
    set(dadanExportJobIdAtom, null);
    set(dadanExportResultAtom, null);
    set(isDadanExportPopoverOpenAtom, false);
    set(dadanExportProjectIdAtom, null);
  }
);

// Action atom: Update Dadan export progress
export const updateDadanExportProgressAtom = atom(
  null,
  (get, set, update: { status: DadanExportStatus; progress: number; message?: string }) => {
    set(dadanExportStatusAtom, update.status);
    set(dadanExportProgressAtom, update.progress);
    if (update.message) {
      set(dadanExportProgressMessageAtom, update.message);
    }
  }
);

// Action atom: Complete Dadan export with result
export const completeDadanExportAtom = atom(
  null,
  (get, set, result: DadanExportResult) => {
    set(dadanExportStatusAtom, result.success ? 'complete' : 'failed');
    set(dadanExportProgressAtom, result.success ? 100 : get(dadanExportProgressAtom));
    set(dadanExportResultAtom, result);
  }
);
